import RichiesteFatturazioneTab from "./contabilita/RichiesteFatturazioneTab";
import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/api";
import { BookOpen, Filter, FileDown } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";
import RateFornitoriTab from "./contabilita/RateFornitoriTab";

const eur = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(n || 0);
const COLORS = ["#ea580c", "#0f172a", "#2563eb", "#16a34a", "#eab308", "#7c3aed", "#dc2626", "#0891b2", "#db2777"];

const TIPO_LABEL = {
  ricavo: "Ricavi",
  costo_diretto: "Costi diretti",
  costo_fisso: "Costi di gestione",
  credito: "Crediti",
  debito: "Debiti",
  iva: "IVA",
  altro: "Altri",
};
const TIPO_COLOR = {
  ricavo: "bg-emerald-100 text-emerald-800 border-emerald-200",
  costo_diretto: "bg-orange-100 text-orange-800 border-orange-200",
  costo_fisso: "bg-blue-100 text-blue-800 border-blue-200",
  credito: "bg-slate-100 text-slate-800 border-slate-200",
  debito: "bg-red-100 text-red-800 border-red-200",
  iva: "bg-yellow-100 text-yellow-800 border-yellow-200",
};

export default function Contabilita() {
  const initialTab = new URLSearchParams(window.location.search).get("tab") || "riepilogo";
  const [tab, setTab] = useState(initialTab);
  const [filters, setFilters] = useState({ da: "", a: "", cantiere_id: "", fornitore: "", lavoratore_id: "", codice: "", societa: "" });

  const params = useMemo(
    () => new URLSearchParams(Object.fromEntries(Object.entries(filters).filter(([, v]) => v))).toString(),
    [filters]
  );

  const { data: piano = [] } = useQuery({
    queryKey: ["piano-conti"],
    queryFn: () => api.get("/contabilita/piano-conti").then((r) => r.data),
  });
  const { data: riepilogo } = useQuery({
    queryKey: ["riepilogo", params],
    queryFn: () => api.get(`/contabilita/riepilogo${params ? `?${params}` : ""}`).then((r) => r.data),
  });
  const { data: movimenti = [] } = useQuery({
    queryKey: ["movimenti", params],
    queryFn: () => api.get(`/contabilita/movimenti${params ? `?${params}` : ""}`).then((r) => r.data),
    enabled: tab === "movimenti",
  });
  const { data: cantieri = [] } = useQuery({
    queryKey: ["cantieri"],
    queryFn: () => api.get("/cantieri").then((r) => r.data),
  });
  const { data: lavoratori = [] } = useQuery({
    queryKey: ["lavoratori"],
    queryFn: () => api.get("/lavoratori").then((r) => r.data),
  });

  const fornitori = useMemo(
    () => Array.from(new Set(movimenti.map((m) => m.controparte).filter(Boolean))).sort(),
    [movimenti]
  );

  const chartData = useMemo(() => {
    if (!riepilogo) return [];
    // Ripartizione costi per tipo
    const agg = {};
    for (const r of riepilogo.righe) {
      if (r.tipo === "costo_diretto" || r.tipo === "costo_fisso") {
        agg[r.descrizione] = (agg[r.descrizione] || 0) + Math.abs(r.dare);
      }
    }
    return Object.entries(agg).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [riepilogo]);

  return (
    <div className="space-y-6">
      <div>
        <div className="overline">Amministrazione</div>
        <h1 className="font-display text-3xl xl:text-4xl font-black flex items-center gap-3">
          <BookOpen className="w-7 h-7 text-orange-600" /> Contabilità
        </h1>
        <p className="text-slate-600 text-sm mt-1">
          Registrazioni automatiche generate da bolle, rapportini e costi fissi con codice del piano dei conti.
        </p>
      </div>

      {/* Filtri */}
      <div className="bg-white border border-slate-200 p-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-slate-600" />
          <div className="overline">Filtri</div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
          <FInput label="Dal" type="date" value={filters.da} onChange={(v) => setFilters({ ...filters, da: v })} />
          <FInput label="Al" type="date" value={filters.a} onChange={(v) => setFilters({ ...filters, a: v })} />
          <FSelect label="Cantiere" value={filters.cantiere_id} onChange={(v) => setFilters({ ...filters, cantiere_id: v })}
            options={[["", "Tutti"], ...cantieri.map((c) => [c.cantiere_id, c.nome])]} />
          <FSelect label="Fornitore" value={filters.fornitore} onChange={(v) => setFilters({ ...filters, fornitore: v })}
            options={[["", "Tutti"], ...fornitori.map((f) => [f, f])]} />
          <FSelect label="Lavoratore" value={filters.lavoratore_id} onChange={(v) => setFilters({ ...filters, lavoratore_id: v })}
            options={[["", "Tutti"], ...lavoratori.map((l) => [l.lavoratore_id, `${l.nome} ${l.cognome}`])]} />
          <FSelect label="Codice contabile" value={filters.codice} onChange={(v) => setFilters({ ...filters, codice: v })}
            options={[["", "Tutti"], ...piano.map((p) => [p.codice, `${p.codice} — ${p.descrizione}`])]} />
          <FInput label="Società" value={filters.societa} onChange={(v) => setFilters({ ...filters, societa: v })} />
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200 flex flex-wrap gap-1">
        {["riepilogo", "movimenti", "piano", "rate-fornitori", "richieste-fatturazione"].map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            data-testid={`contabilita-tab-${t}`}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              tab === t ? "border-orange-600 text-slate-900" : "border-transparent text-slate-600 hover:text-slate-900"
            }`}
          >
            {t === "riepilogo" ? "Riepilogo per codice" : t === "movimenti" ? "Movimenti" : t === "piano" ? "Piano dei conti" : t === "rate-fornitori" ? "💰 Rate fornitori" : "Richieste fatturazione"}
          </button>
        ))}
      </div>

      {tab === "rate-fornitori" && <RateFornitoriTab />}
      {tab === "richieste-fatturazione" && <RichiesteFatturazioneTab />}

      {/* Riepilogo */}
      {tab === "riepilogo" && riepilogo && (
        <div className="space-y-6">
          <div className="grid md:grid-cols-3 gap-3">
            <StatCard label="Totale DARE (costi/crediti)" value={eur(riepilogo.totale_dare)} />
            <StatCard label="Totale AVERE (ricavi/debiti)" value={eur(riepilogo.totale_avere)} />
            <StatCard label="Saldo (dare − avere)" value={eur(riepilogo.totale_dare - riepilogo.totale_avere)} highlight />
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 bg-white border border-slate-200 overflow-x-auto">
              <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                <div>
                  <div className="overline">Riepilogo per codice contabile</div>
                  <h3 className="font-display font-bold">{riepilogo.righe.length} voci di contabilità</h3>
                </div>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="text-left p-3">Codice</th>
                    <th className="text-left p-3">Descrizione</th>
                    <th className="text-left p-3">Tipo</th>
                    <th className="text-right p-3">Movimenti</th>
                    <th className="text-right p-3">Dare €</th>
                    <th className="text-right p-3">Avere €</th>
                    <th className="text-right p-3">Saldo €</th>
                  </tr>
                </thead>
                <tbody>
                  {riepilogo.righe.map((r) => (
                    <tr key={r.codice} className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="p-3 font-mono font-bold text-orange-700">{r.codice}</td>
                      <td className="p-3">{r.descrizione}</td>
                      <td className="p-3">
                        <span className={`text-[10px] uppercase font-bold px-2 py-0.5 border rounded-sm ${TIPO_COLOR[r.tipo] || ""}`}>
                          {TIPO_LABEL[r.tipo] || r.tipo}
                        </span>
                      </td>
                      <td className="p-3 text-right font-mono text-slate-500">{r.count}</td>
                      <td className="p-3 text-right font-mono">{eur(r.dare)}</td>
                      <td className="p-3 text-right font-mono">{eur(r.avere)}</td>
                      <td className={`p-3 text-right font-mono font-bold ${r.saldo >= 0 ? "text-slate-900" : "text-red-700"}`}>
                        {eur(r.saldo)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-slate-900 text-white">
                  <tr>
                    <td className="p-3 font-display font-bold" colSpan="4">TOTALE</td>
                    <td className="p-3 text-right font-mono font-bold">{eur(riepilogo.totale_dare)}</td>
                    <td className="p-3 text-right font-mono font-bold">{eur(riepilogo.totale_avere)}</td>
                    <td className="p-3 text-right font-mono font-black">{eur(riepilogo.totale_dare - riepilogo.totale_avere)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <div className="bg-white border border-slate-200 p-5">
              <div className="overline mb-3">Ripartizione costi</div>
              {chartData.length > 0 ? (
                <div className="w-full h-72">
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie data={chartData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={false}>
                        {chartData.map((entry, i) => <Cell key={entry.name} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip formatter={(v) => eur(v)} />
                      <Legend wrapperStyle={{ fontSize: 10 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="text-slate-400 text-sm">Nessun dato</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Movimenti */}
      {tab === "movimenti" && (
        <div className="bg-white border border-slate-200 overflow-x-auto">
          <div className="p-4 border-b border-slate-200">
            <div className="overline">Movimenti contabili</div>
            <h3 className="font-display font-bold">{movimenti.length} registrazioni</h3>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left p-3">Data</th>
                <th className="text-left p-3">Codice</th>
                <th className="text-left p-3">Descrizione</th>
                <th className="text-left p-3">Cantiere</th>
                <th className="text-left p-3">Controparte</th>
                <th className="text-left p-3">Fonte</th>
                <th className="text-right p-3">Dare €</th>
                <th className="text-right p-3">Avere €</th>
              </tr>
            </thead>
            <tbody>
              {movimenti.map((m) => (
                <tr key={`${m.data}-${m.codice}-${m.fonte_id}-${m.dare}-${m.avere}`} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="p-3 font-mono text-xs">{m.data}</td>
                  <td className="p-3 font-mono font-bold text-orange-700">{m.codice}</td>
                  <td className="p-3">{m.descrizione}</td>
                  <td className="p-3 text-xs text-slate-600">{m.cantiere_nome || "-"}</td>
                  <td className="p-3 text-xs">{m.controparte || "-"}</td>
                  <td className="p-3">
                    <span className="text-[10px] uppercase font-bold px-2 py-0.5 bg-slate-100 border border-slate-200 rounded-sm">
                      {m.fonte}
                    </span>
                  </td>
                  <td className="p-3 text-right font-mono">{m.dare ? eur(m.dare) : ""}</td>
                  <td className="p-3 text-right font-mono">{m.avere ? eur(m.avere) : ""}</td>
                </tr>
              ))}
              {movimenti.length === 0 && (
                <tr><td colSpan="8" className="p-8 text-center text-slate-400">Nessun movimento nel periodo/filtri selezionati</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Piano dei conti */}
      {tab === "piano" && (
        <div className="bg-white border border-slate-200 overflow-x-auto">
          <div className="p-4 border-b border-slate-200">
            <div className="overline">Piano dei conti</div>
            <h3 className="font-display font-bold">{piano.length} codici</h3>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left p-3">Codice</th>
                <th className="text-left p-3">Descrizione</th>
                <th className="text-left p-3">Tipo</th>
                <th className="text-left p-3">Natura</th>
              </tr>
            </thead>
            <tbody>
              {piano.map((p) => (
                <tr key={p.codice} className="border-t border-slate-100">
                  <td className="p-3 font-mono font-bold text-orange-700">{p.codice}</td>
                  <td className="p-3">{p.descrizione}</td>
                  <td className="p-3">
                    <span className={`text-[10px] uppercase font-bold px-2 py-0.5 border rounded-sm ${TIPO_COLOR[p.tipo] || "bg-slate-100 border-slate-200"}`}>
                      {TIPO_LABEL[p.tipo] || p.tipo}
                    </span>
                  </td>
                  <td className="p-3 text-xs uppercase tracking-wider text-slate-500">{p.natura}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function FInput({ label, type = "text", value, onChange }) {
  return (
    <div>
      <label className="text-xs font-semibold text-slate-600 block mb-1">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full px-2 py-1.5 border border-slate-300 rounded-sm text-xs" />
    </div>
  );
}
function FSelect({ label, value, onChange, options }) {
  return (
    <div>
      <label className="text-xs font-semibold text-slate-600 block mb-1">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full px-2 py-1.5 border border-slate-300 rounded-sm text-xs">
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}
function StatCard({ label, value, highlight }) {
  return (
    <div className={`bg-white border p-4 ${highlight ? "border-orange-500" : "border-slate-200"}`}>
      <div className="overline">{label}</div>
      <div className="font-display font-black text-xl mt-1">{value}</div>
    </div>
  );
}
